import { useState, useRef } from 'react';
import emailjs from '@emailjs/browser';

const SERVICE_ID = 'service_qfeghpa';
const TEMPLATE_ID = 'template_f7xu87m';
const PUBLIC_KEY = 'KguKQOUjZI6pt_RoY';

const emptyForm = () => ({
  from_name: '',
  from_email: '',
  company: '',
  phone: '',
  message: '',
});

function Contact() {
  const formRef = useRef();
  const [formData, setFormData] = useState(emptyForm);
  const [lastSubmittedEmail, setLastSubmittedEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};
    if (!formData.from_name.trim()) newErrors.from_name = 'Name is required.';
    if (!formData.from_email.trim()) {
      newErrors.from_email = 'Email is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.from_email)) {
      newErrors.from_email = 'Please enter a valid email address.';
    }
    if (!formData.company.trim()) newErrors.company = 'Company is required.';
    if (!formData.message.trim()) newErrors.message = 'Message is required.';
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setStatus('sending');
    try {
      await emailjs.sendForm(SERVICE_ID, TEMPLATE_ID, formRef.current, PUBLIC_KEY);
      setLastSubmittedEmail(formData.from_email);
      setStatus('success');
      setFormData(emptyForm());
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="contact-page">
      {/* Hero */}
      <section className="hero">
        <div className="hero-content">
          <h1 className="hero-title">Get in Touch</h1>
          <p className="hero-subtitle">
            Ready to transform your event technology? We'd love to hear from you.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="contact-section">
        <div className="container">
          <div className="contact-single">
            <div className="contact-intro-block">
              <h2 className="contact-info-title">Let's Talk</h2>
              <p className="contact-info-text">
                Whether you're planning your first event or looking to upgrade your
                existing technology stack, our team is here to help you create
                seamless, engaging experiences.
              </p>
            </div>

            <div className="contact-form-wrapper">
              {status === 'success' ? (
                <div className="contact-success">
                  <div className="contact-success-icon" aria-hidden="true" />
                  <h3>Message Sent!</h3>
                  <p>
                    Thanks for reaching out. We'll get back to you at{' '}
                    <strong>{lastSubmittedEmail || 'your email'}</strong> within 24 hours.
                  </p>
                  <button
                    className="cta-button"
                    onClick={() => setStatus('idle')}
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form ref={formRef} className="contact-form" onSubmit={handleSubmit} noValidate>
                  <input type="hidden" name="to_email" value="noah@metinc.tech" />

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="from_name" className="form-label">
                        Full Name <span className="required">*</span>
                      </label>
                      <input
                        id="from_name"
                        type="text"
                        name="from_name"
                        className={`form-input ${errors.from_name ? 'form-input-error' : ''}`}
                        placeholder="Jane Smith"
                        value={formData.from_name}
                        onChange={handleChange}
                      />
                      {errors.from_name && (
                        <span className="form-error">{errors.from_name}</span>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor="from_email" className="form-label">
                        Email Address <span className="required">*</span>
                      </label>
                      <input
                        id="from_email"
                        type="email"
                        name="from_email"
                        className={`form-input ${errors.from_email ? 'form-input-error' : ''}`}
                        placeholder="jane@company.com"
                        value={formData.from_email}
                        onChange={handleChange}
                      />
                      {errors.from_email && (
                        <span className="form-error">{errors.from_email}</span>
                      )}
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="company" className="form-label">
                        Company / Organization <span className="required">*</span>
                      </label>
                      <input
                        id="company"
                        type="text"
                        name="company"
                        className={`form-input ${errors.company ? 'form-input-error' : ''}`}
                        placeholder="Acme Events Inc."
                        value={formData.company}
                        onChange={handleChange}
                      />
                      {errors.company && (
                        <span className="form-error">{errors.company}</span>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor="phone" className="form-label">
                        Phone <span className="form-optional">(optional)</span>
                      </label>
                      <input
                        id="phone"
                        type="tel"
                        name="phone"
                        className="form-input"
                        placeholder="+1 (555) 000-0000"
                        value={formData.phone}
                        onChange={handleChange}
                        autoComplete="tel"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="message" className="form-label">
                      Message <span className="required">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      className={`form-input form-textarea ${errors.message ? 'form-input-error' : ''}`}
                      placeholder="Tell us about your event and what you're looking for..."
                      value={formData.message}
                      onChange={handleChange}
                      rows={6}
                    />
                    {errors.message && (
                      <span className="form-error">{errors.message}</span>
                    )}
                  </div>

                  {status === 'error' && (
                    <div className="form-submit-error">
                      Something went wrong. Please try again or email us directly at{' '}
                      <a href="mailto:noah@metinc.tech">noah@metinc.tech</a>.
                    </div>
                  )}

                  <button
                    type="submit"
                    className="cta-button contact-submit"
                    disabled={status === 'sending'}
                  >
                    {status === 'sending' ? 'Sending…' : 'Send Message'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Contact;
